import './ShipmentShowLogistic.scss';

import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { DateField, TextField } from '@refinedev/antd';
import { Button, Flex, Form, Input, Modal, Space, Tooltip } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LinkTextField from '@/components/general/LinkTextField';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ShipmentShowLogisticProps {
  record?: Shipment;
  refetch?: () => void;
}

const ShipmentShowLogistic = (props: ShipmentShowLogisticProps) => {
  const { t } = useTranslation();
  const { userWorkArea } = useUserContext();
  const [openModal, setOpenModal] = useState<{ tracking: boolean; delivery: boolean }>({
    tracking: false,
    delivery: false,
  });
  const { record } = props;
  const closeModal = useCallback(() => setOpenModal({ tracking: false, delivery: false }), []);

  return (
    <div className="ShipmentShowDataCard">
      <div className="ShipmentShowData">
        <b>{t('Fecha de envío efectiva')}</b>
        {record?.date ? <DateField value={record?.date} /> : '-'}
      </div>
      <div className="ShipmentShowData">
        <Flex gap={6}>
          <b>{t('Número de seguimiento')}</b>
          {userWorkArea == USER_WORK_AREA.LOGISTICS &&
            record?.status != PACKAGE_STATES.FINALIZADO && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => setOpenModal({ tracking: true, delivery: false })}
              />
            )}
        </Flex>
        <TextField value={record?.tracking_number ? record?.tracking_number : '-'} />
      </div>
      <div className="ShipmentShowData">
        <b>{t('Fecha de recepción del cliente')}</b>
        {record?.date_finished ? <DateField value={record?.date_finished} /> : '-'}
      </div>
      {record?.tracking_number && record?.shipper?.tracking_link && (
        <LinkTextField
          to={`${record?.shipper?.tracking_link}/${record?.tracking_number}`}
          label={t('Ver seguimiento')}
          external
        />
      )}

      <div className="ShipmentShowData">
        <Flex gap={6}>
          <b>{t('Números de remito')}</b>
          {userWorkArea == USER_WORK_AREA.LOGISTICS &&
            record?.status != PACKAGE_STATES.FINALIZADO && (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => setOpenModal({ tracking: false, delivery: true })}
              />
            )}
        </Flex>
        {record?.delivery_note ? (
          <ul style={{ paddingInlineStart: 24 }}>
            {record?.delivery_note?.split(',').map((number, index) =>
              record?.odoo_delivery_link?.[index] ? (
                <li key={index}>
                  <LinkTextField external to={record?.odoo_delivery_link[index]} label={number} />
                </li>
              ) : (
                <li key={index}>
                  <div className="DeliveryNumberItem">{number} </div>
                </li>
              ),
            )}
          </ul>
        ) : (
          '-'
        )}
      </div>

      <Modal
        destroyOnHidden
        open={openModal.tracking || openModal.delivery}
        footer={null}
        onCancel={() => closeModal()}
        title={
          openModal.tracking ? t('Editar número de seguimiento') : t('Editar número de remito')
        }>
        {openModal.tracking && (
          <ModifyTrackingNumberModal
            record={record}
            refetch={props.refetch}
            closeModal={closeModal}
          />
        )}
        {openModal.delivery && (
          <ModifyDeliveryNumbersModal
            record={record}
            refetch={props.refetch}
            closeModal={closeModal}
          />
        )}
      </Modal>
    </div>
  );
};

interface ModifyTrackingNumberModalProps {
  record?: Shipment;
  refetch?: () => void;
  closeModal: () => void;
}

const ModifyTrackingNumberModal = (props: ModifyTrackingNumberModalProps) => {
  const { t } = useTranslation();
  const { record, refetch, closeModal } = props;
  const { shipmentRepository } = useRepositoriesContext();
  const [newTrackingNumber, setNewTrackingNumber] = useState(record?.tracking_number);
  const onConfirmChange = useCallback(() => {
    if (record?.id) {
      if (newTrackingNumber === record?.tracking_number) {
        return;
      } else if (newTrackingNumber != undefined) {
        void shipmentRepository
          .updateShipment(record?.id, { tracking_number: newTrackingNumber })
          .then(() => {
            closeModal();
            refetch?.();
          });
      }
    }
  }, [record, newTrackingNumber, shipmentRepository, closeModal, refetch]);

  return (
    <div className="ModifyTrackingNumberModal">
      <Form onFinish={onConfirmChange} layout="vertical">
        <Form.Item>
          <Input
            defaultValue={record?.tracking_number}
            value={newTrackingNumber}
            onChange={e => setNewTrackingNumber(e.target.value)}
          />
        </Form.Item>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Button htmlType="submit" type="primary">
            {t('Guardar')}
          </Button>
        </Flex>
      </Form>
    </div>
  );
};

const ModifyDeliveryNumbersModal = (props: ModifyTrackingNumberModalProps) => {
  const { t } = useTranslation();
  const { record, closeModal, refetch } = props;
  const { shipmentRepository } = useRepositoriesContext();
  const [form] = Form.useForm();
  const [newDeliveryNumber, setNewDeliveryNumber] = useState(
    record?.delivery_note?.split(',') || [],
  );
  const onConfirmChange = useCallback(() => {
    if (record?.id) {
      if (newDeliveryNumber.join(',') === record?.delivery_note) {
        return;
      } else if (newDeliveryNumber != undefined) {
        void shipmentRepository
          .updateShipment(record?.id, { delivery_note: newDeliveryNumber.join(',') })
          .then(() => {
            closeModal();
            refetch?.();
          });
      }
    }
  }, [record, newDeliveryNumber, shipmentRepository, closeModal, refetch]);
  const deliveryInputValue = Form.useWatch('deliveryNumber', form);

  return (
    <div className="ModifyDeliveryNumberModal">
      <Form
        form={form}
        onFinish={values => {
          setNewDeliveryNumber(prevValue =>
            values.deliveryNumber == '' || values.deliveryNumber == undefined
              ? prevValue
              : [...prevValue, values.deliveryNumber],
          );
          form.resetFields();
        }}
        layout="vertical">
        <Form.Item
          name="deliveryNumber"
          rules={[
            {
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                if (newDeliveryNumber.includes(value)) {
                  return Promise.reject(new Error(t('El número de remito ya existe en la lista')));
                }

                return Promise.resolve();
              },
            },
          ]}>
          <Space.Compact style={{ width: '100%' }}>
            <Input />
            <Button htmlType="submit"> + </Button>
          </Space.Compact>
        </Form.Item>
        <ul>
          {newDeliveryNumber.map((number, index) => (
            <li key={index}>
              <div className="DeliveryNumberItem">
                {number}{' '}
                <Button
                  onClick={() =>
                    setNewDeliveryNumber(prevValue => prevValue.filter(n => n != number))
                  }
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </div>
            </li>
          ))}
        </ul>
        <Flex justify="end" gap={8}>
          <Button onClick={closeModal}>{t('Cancelar')}</Button>
          <Tooltip
            title={
              deliveryInputValue != undefined && deliveryInputValue != ''
                ? 'Finalice la edición del numero de remito para guardar'
                : ''
            }>
            <Button
              onClick={onConfirmChange}
              type="primary"
              disabled={deliveryInputValue != undefined && deliveryInputValue != ''}>
              {t('Guardar')}
            </Button>
          </Tooltip>
        </Flex>
      </Form>
    </div>
  );
};

export default ShipmentShowLogistic;
